import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';

function CreateFromEmailModal({ email, onClose, onCreated, token }) {
  const [participants, setParticipants] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [leadStatus, setLeadStatus] = useState('new');
  const [leadIndustry, setLeadIndustry] = useState('');
  const [leadSource, setLeadSource] = useState('Email Campaign');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  const INDUSTRIES = ['E-commerce', 'Technology', 'Retail', 'Healthcare', 'Finance', 'Manufacturing', 'Education', 'Real Estate', 'Consulting', 'Media & Entertainment', 'Transportation', 'Hospitality', 'Telecommunications', 'Other'];
  const SOURCES = ['Website', 'Contact Form', 'Get a Demo', 'Get a Call Back', 'LinkedIn', 'Referral', 'Cold Call', 'Email Campaign', 'Conference', 'Social Media', 'Self', 'Marketing', 'Internal Ref', 'Client Reference', 'Existing Client', 'Churned Client', 'Partner- India', 'Partner- Intl', 'OEM-VISPL', 'OEM-OXT', 'Other'];

  // Common email domains to skip for company detection
  const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com', 'icloud.com', 'aol.com', 'protonmail.com', 'mail.com', 'ymail.com'];

  // Parse email address - handles "Name <email>" and plain "email" formats
  const parseEmailAddress = (addr) => {
    if (!addr) return null;
    const str = String(addr).trim();
    const match = str.match(/^(.+?)\s*<(.+?)>$/);
    if (match) {
      return { name: match[1].trim(), email: match[2].trim().toLowerCase() };
    }
    // Plain email
    const email = str.toLowerCase();
    const namePart = email.split('@')[0] || '';
    const name = namePart.replace(/[._]/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    return { name, email };
  };

  // Extract company from email domain
  const getCompanyFromEmail = (emailAddr) => {
    if (!emailAddr) return '';
    const domain = emailAddr.split('@')[1] || '';
    if (commonDomains.includes(domain.toLowerCase())) return '';
    const companyPart = domain.split('.')[0] || '';
    return companyPart.charAt(0).toUpperCase() + companyPart.slice(1);
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ').filter(p => p);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (parts[0]?.[0] || '?').toUpperCase();
  };

  // Initialize participants from email
  useEffect(() => {
    const allParticipants = [];
    const seenEmails = new Set();

    // Determine folder type
    const isSentFolder = email.folder === 'sent' || email.folder === 'sentitems';

    // Add sender (From) - for inbox emails, this is typically the lead
    if (email.from_email) {
      const fromParsed = { name: email.from_name || '', email: email.from_email.toLowerCase() };
      if (!fromParsed.name) {
        const parsed = parseEmailAddress(email.from_email);
        fromParsed.name = parsed?.name || '';
      }
      if (!seenEmails.has(fromParsed.email)) {
        seenEmails.add(fromParsed.email);
        allParticipants.push({
          email: fromParsed.email,
          name: fromParsed.name,
          company: getCompanyFromEmail(fromParsed.email),
          type: 'from',
          selected: true,
          createType: isSentFolder ? 'contact' : 'lead'  // In sent folder, sender is us, so default to contact
        });
      }
    }

    // Add To recipients
    const toList = email.to_recipients || [];
    toList.forEach((recipient, idx) => {
      const parsed = parseEmailAddress(recipient);
      if (parsed && !seenEmails.has(parsed.email)) {
        seenEmails.add(parsed.email);
        allParticipants.push({
          email: parsed.email,
          name: parsed.name,
          company: getCompanyFromEmail(parsed.email),
          type: 'to',
          selected: true,
          createType: isSentFolder && idx === 0 ? 'lead' : 'contact'  // In sent folder, first recipient is the lead
        });
      }
    });

    // Add CC recipients
    const ccList = email.cc_recipients || [];
    ccList.forEach(recipient => {
      const parsed = parseEmailAddress(recipient);
      if (parsed && !seenEmails.has(parsed.email)) {
        seenEmails.add(parsed.email);
        allParticipants.push({
          email: parsed.email,
          name: parsed.name,
          company: getCompanyFromEmail(parsed.email),
          type: 'cc',
          selected: true,
          createType: 'contact'
        });
      }
    });

    setParticipants(allParticipants);
  }, [email]);

  // Handle select all
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setParticipants(prev => prev.map(p => ({ ...p, selected: checked })));
  };

  // Handle individual selection
  const handleParticipantSelect = (index, checked) => {
    setParticipants(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], selected: checked };
      return updated;
    });
    // Update selectAll based on all selections
    const allSelected = participants.every((p, i) => i === index ? checked : p.selected);
    setSelectAll(allSelected);
  };

  // Handle create type change
  const handleCreateTypeChange = (index, createType) => {
    setParticipants(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], createType };
      return updated;
    });
  };

  // Handle create
  const handleCreate = async () => {
    const selectedParticipants = participants
      .filter(p => p.selected && p.createType !== 'skip')
      .map(p => ({
        email: p.email,
        name: p.name,
        company: p.company,
        create_type: p.createType
      }));

    if (selectedParticipants.length === 0) {
      alert('Please select at least one participant to create');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/email/bulk-create-from-email`, {
        email_id: email.id,
        participants: selectedParticipants,
        lead_status: leadStatus,
        lead_industry: leadIndustry,
        lead_source: leadSource
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResult(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create records');
    } finally {
      setCreating(false);
    }
  };

  // If we have a result, show summary
  if (result) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-green-600">Records Created</h2>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-slate-700 dark:text-slate-300">{result.summary}</p>
            
            {result.leads_created?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Leads:</p>
                <ul className="text-sm text-slate-500 dark:text-slate-400 ml-4 list-disc">
                  {result.leads_created.map(l => (
                    <li key={l.id}>{l.name} ({l.email})</li>
                  ))}
                </ul>
              </div>
            )}
            
            {result.contacts_created?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Contacts:</p>
                <ul className="text-sm text-slate-500 dark:text-slate-400 ml-4 list-disc">
                  {result.contacts_created.map(c => (
                    <li key={c.id}>{c.name} ({c.email})</li>
                  ))}
                </ul>
              </div>
            )}

            {result.companies_created?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Companies:</p>
                <ul className="text-sm text-slate-500 dark:text-slate-400 ml-4 list-disc">
                  {result.companies_created.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                <p className="text-sm font-medium text-yellow-700 dark:text-yellow-300">Notes:</p>
                <ul className="text-xs text-yellow-600 dark:text-yellow-400">
                  {result.errors.map((e, i) => (
                    <li key={`email-err-${i}`}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
            <button 
              onClick={() => onCreated(result)} 
              className="btn-primary"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold">Create from Email</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Select participants to create as Leads or Contacts</p>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-4">
          {/* Email Subject Info */}
          <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
            <p className="text-sm text-slate-600 dark:text-slate-400 truncate">
              <span className="font-medium">Subject:</span> {email.subject || '(No subject)'}
            </p>
          </div>

          {/* Lead Status Selection */}
          <div>
            <label className="form-label">Lead Status (for new leads)</label>
            <select
              value={leadStatus}
              onChange={(e) => setLeadStatus(e.target.value)}
              className="input-field w-full"
              data-testid="email-lead-status"
            >
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
            </select>
          </div>

          {/* Industry and Source */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Industry</label>
              <select
                value={leadIndustry}
                onChange={(e) => setLeadIndustry(e.target.value)}
                className="input-field w-full"
                data-testid="email-lead-industry"
              >
                <option value="">Select Industry</option>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Source</label>
              <select
                value={leadSource}
                onChange={(e) => setLeadSource(e.target.value)}
                className="input-field w-full"
                data-testid="email-lead-source"
              >
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Select All Header */}
          <div className="flex items-center gap-3 p-2 bg-slate-100 rounded-lg">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
              data-testid="select-all-checkbox"
            />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Select All Participants</span>
            <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
              {participants.filter(p => p.selected).length} of {participants.length} selected
            </span>
          </div>

          {/* Participants List */}
          <div className="space-y-2">
            {participants.map((participant, index) => (
              <div 
                key={participant.email}
                className={`flex items-center gap-3 p-3 border rounded-lg transition ${
                  participant.selected ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 opacity-60'
                }`}
                data-testid={`participant-row-${index}`}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={participant.selected}
                  onChange={(e) => handleParticipantSelect(index, e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                />

                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                  participant.type === 'from' ? 'bg-blue-100 text-blue-700' :
                  participant.type === 'to' ? 'bg-green-100 text-green-700' :
                  'bg-slate-100 text-slate-700 dark:text-slate-300'
                }`}>
                  {getInitials(participant.name)}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{participant.name}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      participant.type === 'from' ? 'bg-blue-100 text-blue-700' :
                      participant.type === 'to' ? 'bg-green-100 text-green-700' :
                      'bg-slate-100 text-slate-600 dark:text-slate-400'
                    }`}>
                      {participant.type.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{participant.email}</p>
                  {participant.company && (
                    <p className="text-xs text-slate-400">Company: {participant.company}</p>
                  )}
                </div>

                {/* Create Type Dropdown */}
                <select
                  value={participant.createType}
                  onChange={(e) => handleCreateTypeChange(index, e.target.value)}
                  disabled={!participant.selected}
                  className={`text-sm border rounded-lg px-2 py-1.5 ${
                    participant.selected ? 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800' : 'border-slate-200 dark:border-slate-700 bg-slate-100'
                  }`}
                  data-testid={`create-type-${index}`}
                >
                  <option value="lead">Lead</option>
                  <option value="contact">Contact</option>
                  <option value="skip">Skip</option>
                </select>
              </div>
            ))}

            {participants.length === 0 && (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <p>No participants found in this email</p>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {participants.filter(p => p.selected && p.createType === 'lead').length} Lead(s), {' '}
            {participants.filter(p => p.selected && p.createType === 'contact').length} Contact(s)
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button 
              onClick={handleCreate} 
              disabled={creating || participants.filter(p => p.selected && p.createType !== 'skip').length === 0} 
              className="btn-primary"
              data-testid="create-from-email-submit"
            >
              {creating ? 'Creating...' : 'Create Records'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateFromEmailModal;
